import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, AdminReportListItem } from '../../api/admin.api';

interface ReportDetailModalProps {
  report: AdminReportListItem | null;
  onClose: () => void;
}

export const ReportDetailModal: React.FC<ReportDetailModalProps> = ({
  report,
  onClose,
}) => {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (reportId: number) => adminApi.processReportDelete(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminReports'] });
      alert('신고가 처리되었습니다. 질문이 삭제되었습니다.');
      onClose();
    },
    onError: () => {
      alert('신고 처리에 실패했습니다.');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (reportId: number) => adminApi.processReportReject(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminReports'] });
      alert('신고가 기각되었습니다.');
      onClose();
    },
    onError: () => {
      alert('신고 기각에 실패했습니다.');
    },
  });

  const handleDelete = () => {
    if (!report) return;
    if (window.confirm('정말로 이 질문을 삭제하시겠습니까?')) {
      deleteMutation.mutate(report.id);
    }
  };

  const handleReject = () => {
    if (!report) return;
    if (window.confirm('이 신고를 기각하시겠습니까?')) {
      rejectMutation.mutate(report.id);
    }
  };

  const getReasonLabel = (reason: string) => {
    switch (reason) {
      case 'PROFANITY':
        return '욕설';
      case 'SPAM':
        return '스팸';
      case 'INAPPROPRIATE':
        return '부적절한내용';
      default:
        return reason;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return '대기중';
      case 'RESOLVED':
        return '처리완료';
      case 'REJECTED':
        return '기각';
      default:
        return status;
    }
  };

  if (!report) return null;

  return (
    <>
      {/* Desktop Modal */}
      <div className="hidden md:block fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-[#E5E7EB]">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#030213]">신고 상세</h2>
              <button
                onClick={onClose}
                className="text-[#6B7280] hover:text-[#030213]"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#6B7280] mb-1">
                신고된 질문
              </label>
              <p className="text-sm text-[#030213]">{report.questionTitle}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#6B7280] mb-1">
                  신고자
                </label>
                <p className="text-sm text-[#030213]">{report.reporterNickname}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6B7280] mb-1">
                  이메일
                </label>
                <p className="text-sm text-[#030213]">{report.reporterEmail}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#6B7280] mb-1">
                  신고 사유
                </label>
                <p className="text-sm text-[#030213]">
                  {getReasonLabel(report.reason)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6B7280] mb-1">
                  상태
                </label>
                <p className="text-sm text-[#030213]">
                  {getStatusLabel(report.status)}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#6B7280] mb-1">
                상세 내용
              </label>
              <p className="text-sm text-[#030213] bg-[#f3f3f5] p-3 rounded">
                {report.details || '상세 내용 없음'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#6B7280] mb-1">
                신고일
              </label>
              <p className="text-sm text-[#030213]">
                {new Date(report.regDateTime).toLocaleString('ko-KR')}
              </p>
            </div>
          </div>

          {report.status === 'PENDING' && (
            <div className="p-6 border-t border-[#E5E7EB] flex gap-3 justify-end">
              <button
                onClick={handleReject}
                disabled={rejectMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-[#030213] bg-[#f3f3f5] rounded-lg hover:bg-[#E5E7EB] transition-colors disabled:opacity-50"
              >
                기각
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                질문 삭제
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Sheet */}
      <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 flex items-end z-50">
        <div className="bg-white rounded-t-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b border-[#E5E7EB]">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-[#030213]">신고 상세</h2>
              <button
                onClick={onClose}
                className="text-[#6B7280] hover:text-[#030213]"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="p-4 space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">
                신고된 질문
              </label>
              <p className="text-sm text-[#030213]">{report.questionTitle}</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">
                신고자
              </label>
              <p className="text-sm text-[#030213]">
                {report.reporterNickname} ({report.reporterEmail})
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">
                  신고 사유
                </label>
                <p className="text-sm text-[#030213]">
                  {getReasonLabel(report.reason)}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">
                  상태
                </label>
                <p className="text-sm text-[#030213]">
                  {getStatusLabel(report.status)}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">
                상세 내용
              </label>
              <p className="text-sm text-[#030213] bg-[#f3f3f5] p-3 rounded">
                {report.details || '상세 내용 없음'}
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">
                신고일
              </label>
              <p className="text-sm text-[#030213]">
                {new Date(report.regDateTime).toLocaleString('ko-KR')}
              </p>
            </div>
          </div>

          {report.status === 'PENDING' && (
            <div className="p-4 border-t border-[#E5E7EB] space-y-2">
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="w-full py-3 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                질문 삭제
              </button>
              <button
                onClick={handleReject}
                disabled={rejectMutation.isPending}
                className="w-full py-3 text-sm font-medium text-[#030213] bg-[#f3f3f5] rounded-lg hover:bg-[#E5E7EB] transition-colors disabled:opacity-50"
              >
                기각
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
